'use client';

import { ChevronDown } from 'lucide-react';
import Link from 'next/link';
import { useState, memo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getLimitedUpcomingEvents, getPopularBlogForMegaMenu } from '@/lib/mega-menu/client-api';
import { Button } from '@/components/ui/Button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/DropdownMenu';
import { useTabContext } from '../context/TabContext';
import { MegaMenuLifetimeMortgagesContent } from '@/content/professionals/mega-menu-content/MegaMenuLifetimeMortgagesContent';
import { MegaMenuResourcesContent } from '@/content/professionals/mega-menu-content/MegaMenuResourcesContent';
import { MegaMenuAboutUsContent } from '@/content/professionals/mega-menu-content/MegaMenuAboutUsContent';
import { MegaMenuNewsContent } from '@/content/professionals/mega-menu-content/MegaMenuNewsContent';
import { MegaMenuEventsContent } from '@/content/professionals/mega-menu-content/MegaMenuEventsContent';
import { CustomButton } from '@/components/ui/CustomButton';
import { usePathname } from 'next/navigation';
import { validLifetimeMortgagePaths } from '@/components/layout/DesktopMenu';
import { MegaMenuEquityReleaseContent } from '@/content/customer/mega-menu-content/MegaMenuEquityReleaseContent';
import { MegaMenuKnowledgeCornerContent } from '@/content/customer/mega-menu-content/MegaMenuKnowledgeCornerContent';
import { MegaMenuExistingCustomersContent } from '@/content/customer/mega-menu-content/MegaMenuExistingCustomersContent';
import { MegaMenuCustomerAboutUsContent } from '@/content/customer/mega-menu-content/MegaMenuCustomerAboutUsContent';
import { MegaMenuMyPureContent } from '@/content/customer/mega-menu-content/MegaMenuMyPureContent';
import { MEGA_MENU_CONFIG, CloseIcon } from '@/constant/mega-menu';

const MegaMenu = memo(function MegaMenu({
  title,
  menuItems,
  footerLinks,
  contactText,
  contactButtonText,
  onContactClick,
}) {
  const { selectedTab } = useTabContext();
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();
  const isLifetimeMortgages = validLifetimeMortgagePaths.includes(pathname);
  const footerLinkMenuData = footerLinks.filter(footer => selectedTab === 'customer' ? footer.label === 'YouTube' : footer );

  // Preload data immediately but only for menus that actually need it
  const shouldFetchBlogs = title === MEGA_MENU_CONFIG.DATA_DEPENDENT_MENUS.NEWS && selectedTab === 'adviser';
  const shouldFetchEvents = title === MEGA_MENU_CONFIG.DATA_DEPENDENT_MENUS.EVENTS && selectedTab === 'adviser';

  // Fetch popular blogs data immediately for News menus with loading state
  const { data: popularBlogs, isLoading: blogsLoading } = useQuery({
    queryKey: ['getPopularBlogForMegaMenu'],
    queryFn: getPopularBlogForMegaMenu,
    staleTime: MEGA_MENU_CONFIG.QUERY_CONFIG.BLOGS.staleTime,
    gcTime: MEGA_MENU_CONFIG.QUERY_CONFIG.BLOGS.gcTime,
    enabled: shouldFetchBlogs,
    refetchOnWindowFocus: false,
  });

  // Fetch upcoming events data immediately for Events menus with loading state
  const { data: upcomingEventData, isLoading: eventsLoading } = useQuery({
    queryKey: ['getLimitedUpcomingEvents', MEGA_MENU_CONFIG.QUERY_CONFIG.EVENTS.limit],
    queryFn: () => getLimitedUpcomingEvents(MEGA_MENU_CONFIG.QUERY_CONFIG.EVENTS.limit),
    staleTime: MEGA_MENU_CONFIG.QUERY_CONFIG.EVENTS.staleTime,
    gcTime: MEGA_MENU_CONFIG.QUERY_CONFIG.EVENTS.gcTime,
    enabled: shouldFetchEvents,
    refetchOnWindowFocus: false,
  });

  return (
    <DropdownMenu open={isOpen} modal={false} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="menu"
          className={` ${isLifetimeMortgages ? 'text-white' : ''} flex items-center space-x-1 xl:justify-evenly ${
            isOpen
              ? `${
                selectedTab === 'adviser' ? 'menu-selected-container menu-selected-text' : 'text-[#F3F4F8] bg-[#A51367]'
              }`
              : ''
          }`}
        >
          {title}{' '}
          <ChevronDown
            className={`ml-1 h-4 w-4 ${isOpen ? 'rotate-180' : ''}`}
          />
        </Button>
      </DropdownMenuTrigger>
      {isOpen && (
        <div
          className="fixed inset-x-0 top-[120px] bottom-0 bg-[#9ca3af80] z-40 xl:top-[10rem] md:top-[147px]"
          onClick={() => setIsOpen(false)}
        />
      )}
      <div className="fixed top-10 left-0 right-0 z-50 flex justify-center">
        <DropdownMenuContent
          className={`relative ${title === 'News' ? 'max-w-[52.5rem] ' : 'max-w-[59.5rem]'} xl:max-w-5xl p-0 bg-white border-none w-fit`}
          sideOffset={55}
        >
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute z-10 right-4 top-2"
              onClick={() => setIsOpen(false)}
            >
              <div className={`${MEGA_MENU_CONFIG.COLORS.border} border-2 rounded-lg px-1 py-1`}>
                <CloseIcon />
              </div>
              <span className="sr-only">Close menu</span>
            </Button>
            {title === 'Lifetime Mortgages' && (<MegaMenuLifetimeMortgagesContent menuItems={menuItems} />)}
            {title === 'Resources' && (<MegaMenuResourcesContent menuItems={menuItems} />)}
            {title === 'About us' && (<MegaMenuAboutUsContent menuItems={menuItems} />)}
            {title === 'News' && (<MegaMenuNewsContent menuItems={menuItems} popularBlogs={popularBlogs} isLoading={blogsLoading} />)}
            {title === 'Events' && (<MegaMenuEventsContent menuItems={menuItems} upcomingEventData={upcomingEventData} isLoading={eventsLoading} />)}
            {title === 'Equity Release' && (<MegaMenuEquityReleaseContent menuItems={menuItems} />)}
            {title === 'Knowledge Corner' && (<MegaMenuKnowledgeCornerContent menuItems={menuItems} />)}
            {title === 'Existing Customers' && (<MegaMenuExistingCustomersContent menuItems={menuItems} />)}
            {title === 'About Us' && (<MegaMenuCustomerAboutUsContent menuItems={menuItems} />)}
            {title === 'MyPure' && (<MegaMenuMyPureContent menuItems={menuItems} />)}

            {(footerLinkMenuData || contactText) && (
              <div
                className={`flex items-center justify-between border-t lg:h-[4.75rem] ${
                  selectedTab === 'adviser' ? MEGA_MENU_CONFIG.COLORS.adviser.background : MEGA_MENU_CONFIG.COLORS.customer.background
                } pt-4 pb-4 pl-10 pr-10 text-white`}
              >
                {footerLinkMenuData && (
                  <div className="flex items-center space-x-4">
                    <Link
                      href={MEGA_MENU_CONFIG.SOCIAL_LINKS.LINKEDIN}
                      className="text-white hover:text-white/90"
                    >
                      <img src="/img/icons/linkedin-white.svg" alt="LinkedIn"/>
                      <span className="sr-only">LinkedIn</span>
                    </Link>

                    <Link
                      href={selectedTab === 'customer'
                        ? MEGA_MENU_CONFIG.SOCIAL_LINKS.YOUTUBE.CUSTOMER
                        : MEGA_MENU_CONFIG.SOCIAL_LINKS.YOUTUBE.ADVISER
                      }
                      className="text-white hover:text-white/90"
                    >
                      <img src="/img/icons/youtube-white.svg" alt="YouTube"/>
                      <span className="sr-only">YouTube</span>
                    </Link>
                  </div>
                )}
                {contactText && (
                  <div className="flex items-center gap-4">
                    {selectedTab === 'adviser' && (
                      <span className="whitespace-nowrap mega-menu-footer-text">{contactText}</span>
                    )}
                    <CustomButton
                      variant="secondaryWhiteSmall"
                      onClick={() => {
                        onContactClick();
                        setIsOpen(false);
                      }}
                    >
                      {contactButtonText || 'Contact us'}
                    </CustomButton>
                  </div>
                )}
              </div>
            )}
          </div>
        </DropdownMenuContent>
      </div>
    </DropdownMenu>
  );
});

export default MegaMenu;
